import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { format } from 'date-fns';
import Popup from '../Popup';
import { TimePeriodOptions } from '../../constants/timePeriodsFilterOptions';
import {
  DetailContainer,
  DetailTitle,
  DetailSection,
  DetailRow,
  DetailLabel,
  DetailValue,
} from './style';

interface AgentPerformanceData {
  name: string;
  email: string;
  stores?: string[];
  avgCRT: string;
  avgCRTFormatted: string;
  maxCRT: string;
  maxCRTFormatted: string;
  minCRT: string;
  minCRTFormatted: string;
  medianCRT: string;
  medianCRTFormatted: string;
  tenMinutesRR: string;
  firstTenRR: string;
}

interface AgentChatPerformanceDetailProps {
  agentPerformanceData: AgentPerformanceData;
  onClose: () => void;
  selectedPeriod: string;
  getDateRange: (period: string) => { startDate: Date; endDate: Date };
}

const ChatPerformanceDetailPopup: React.FC<AgentChatPerformanceDetailProps> = ({ 
  agentPerformanceData, 
  onClose,
  selectedPeriod,
  getDateRange
}) => {
  // Add ESC key handler
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscKey);
    
    return () => {
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [onClose]);

  const renderPeriodInfo = () => {
    const periodLabels = {
      [TimePeriodOptions.LAST_24_HOURS]: <FormattedMessage id="agentPerformances.period.last24Hours" defaultMessage="Last 24 Hours" />,
      [TimePeriodOptions.LAST_7_DAYS]: <FormattedMessage id="agentPerformances.period.last7Days" defaultMessage="Last 7 Days" />,
      [TimePeriodOptions.LAST_30_DAYS]: <FormattedMessage id="agentPerformances.period.last30Days" defaultMessage="Last 30 Days" />,
      [TimePeriodOptions.LAST_3_MONTHS]: <FormattedMessage id="agentPerformances.period.last3Months" defaultMessage="Last 3 Months" />,
      [TimePeriodOptions.LAST_6_MONTHS]: <FormattedMessage id="agentPerformances.period.last6Months" defaultMessage="Last 6 Months" />,
      [TimePeriodOptions.LAST_1_YEAR]: <FormattedMessage id="agentPerformances.period.last1Year" defaultMessage="Last 1 Year" />,
    };
    
    const { startDate, endDate } = getDateRange(selectedPeriod);
    const formattedStartDate = format(startDate, 'do MMM y HH:mm');
    const formattedEndDate = format(endDate, 'do MMM y HH:mm');
    
    return (
      <>
        {formattedStartDate} - {formattedEndDate} ({periodLabels[selectedPeriod]})
      </>
    );
  };

  return (
    <Popup
      isPreviewTicket={false}
      setIsPreviewTicket={() => {}}
      handleClose={onClose}
    >
      <DetailContainer role='dialog' aria-labelledby="Agent Performance Details" aria-modal="true">
        <DetailTitle id="agent-performance-detail-title">
          <FormattedMessage 
            id="agentPerformances.detail.title" 
            defaultMessage="Agent Performance Details" 
          />
        </DetailTitle>
        <DetailSection>
          <h3>
            <FormattedMessage 
              id="agentPerformances.detail.agentInfo" 
              defaultMessage="Agent Information" 
            />
          </h3>
          <DetailRow>
            <DetailLabel>
              <FormattedMessage 
                id="agentPerformances.detail.name" 
                defaultMessage="Name" 
              />
            </DetailLabel>
            <DetailValue>{agentPerformanceData.name}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              <FormattedMessage 
                id="agentPerformances.detail.email" 
                defaultMessage="Email" 
              />
            </DetailLabel>
            <DetailValue>{agentPerformanceData.email}</DetailValue>
          </DetailRow>
          {agentPerformanceData.stores && agentPerformanceData.stores.length > 0 && (
            <DetailRow>
              <DetailLabel>
                <FormattedMessage 
                  id="agentPerformances.detail.stores" 
                  defaultMessage="Stores" 
                />
              </DetailLabel>
              <DetailValue>
                <ul>
                  {agentPerformanceData.stores.map((store, index) => (
                    <li key={index}>{store}</li>
                  ))}
                </ul>
              </DetailValue>
            </DetailRow>
          )}
        </DetailSection>
        
        <DetailSection>
          <h3>
            <FormattedMessage 
              id="agentPerformances.detail.performanceMetrics" 
              defaultMessage="Performance Metrics" 
            />
          </h3>
          <DetailRow>
            <DetailLabel>
              <FormattedMessage 
                id="agentPerformances.detail.period" 
                defaultMessage="Period" 
              />
            </DetailLabel>
            <DetailValue>
              {renderPeriodInfo()}
            </DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              <FormattedMessage 
                id="agentPerformances.detail.avgCRT" 
                defaultMessage="Average CRT" 
              />
            </DetailLabel>
            <DetailValue>{agentPerformanceData?.avgCRTFormatted}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              <FormattedMessage 
                id="agentPerformances.detail.maxCRT" 
                defaultMessage="Maximum CRT" 
              />
            </DetailLabel>
            <DetailValue>{agentPerformanceData?.maxCRTFormatted}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              <FormattedMessage 
                id="agentPerformances.detail.minCRT" 
                defaultMessage="Minimum CRT" 
              />
            </DetailLabel>
            <DetailValue>{agentPerformanceData?.minCRTFormatted}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              <FormattedMessage 
                id="agentPerformances.detail.medianCRT" 
                defaultMessage="Median CRT" 
              />
            </DetailLabel>
            <DetailValue>{agentPerformanceData?.medianCRTFormatted}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              <FormattedMessage 
                id="agentPerformances.detail.tenMinutesRR" 
                defaultMessage="10 Minutes Response Rate" 
              />
            </DetailLabel>
            <DetailValue>{agentPerformanceData.tenMinutesRR}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              <FormattedMessage 
                id="agentPerformances.detail.firstTenRR" 
                defaultMessage="First 10 Response Rate" 
              />
            </DetailLabel>
            <DetailValue>{agentPerformanceData.firstTenRR}</DetailValue>
          </DetailRow>
        </DetailSection>
      </DetailContainer>
    </Popup>
  );
};

export default ChatPerformanceDetailPopup;
